import { BlobWriter, TextReader, ZipWriter } from "@zip.js/zip.js";

export const IDML_MIME_TYPE = "application/vnd.adobe.indesign-idml-package";
const PACKAGE_NAMESPACE =
  "http://ns.adobe.com/AdobeInDesign/idml/1.0/packaging";

type Attributes = Record<string, string | number | boolean>;
export function element(
  parent: Element,
  tag: string,
  attributes: Attributes = {},
): Element {
  const node = parent.ownerDocument.createElement(tag);
  for (const [name, value] of Object.entries(attributes))
    node.setAttribute(name, String(value));
  // InDesign requires Properties before an object’s other child elements.
  if (tag === "Properties") parent.insertBefore(node, parent.firstChild);
  else parent.appendChild(node);
  return node;
}
export function xmlRoot(tag: string, packaged = false): Element {
  const doc = document.implementation.createDocument(
    packaged ? PACKAGE_NAMESPACE : null,
    packaged ? `idPkg:${tag}` : tag,
  );
  const root = doc.documentElement;
  root.setAttribute("DOMVersion", "7.0");
  return root;
}
function xml(root: Element): string {
  return `<?xml version="1.0" encoding="UTF-8"?>\n${new XMLSerializer().serializeToString(root)}`;
}

const SKELETON = {
  Document:
    '<Document DOMVersion="7.0" xmlns:idPkg="http://ns.adobe.com/AdobeInDesign/idml/1.0/packaging" Self="document" ActiveLayer="artwork" ZeroPoint="0 0"><Layer Self="artwork" Name="Artwork" Visible="true" Locked="false" Printable="true" /></Document>',
  Graphic:
    '<Swatch Self="Swatch/None" Name="None" ColorEditable="false" ColorRemovable="false" Visible="true" />',
  Styles:
    '<RootCharacterStyleGroup Self="character_styles"><CharacterStyle Self="CharacterStyle/Normal" Name="Normal" /></RootCharacterStyleGroup><RootParagraphStyleGroup Self="paragraph_styles"><ParagraphStyle Self="ParagraphStyle/Normal" Name="Normal" /></RootParagraphStyleGroup>',
};

export function createIdmlPackage({
  width,
  height,
}: {
  width: number;
  height: number;
}) {
  const root = new DOMParser().parseFromString(
    SKELETON.Document,
    "application/xml",
  ).documentElement;
  const files = new Map<string, Element>();
  function add(path: string, resource: Element) {
    files.set(path, resource);
    element(root, resource.tagName, { src: path });
  }
  const graphic = xmlRoot("Graphic", true);
  graphic.innerHTML = SKELETON.Graphic;
  const styles = xmlRoot("Styles", true);
  styles.innerHTML = SKELETON.Styles;
  const preferences = xmlRoot("Preferences", true);
  // One source pixel is one point, matching a 72 ppi Photoshop document.
  element(preferences, "DocumentPreference", {
    PageWidth: width,
    PageHeight: height,
    FacingPages: false,
    PagesPerDocument: 1,
  });
  const fonts = xmlRoot("Fonts", true);
  for (const [name, resource] of Object.entries({
    Graphic: graphic,
    Preferences: preferences,
    Styles: styles,
    Fonts: fonts,
  })) {
    add(`Resources/${name}.xml`, resource);
  }
  const spreadPackage = xmlRoot("Spread", true);
  const spread = element(spreadPackage, "Spread", {
    Self: "spread",
    PageCount: 1,
    BindingLocation: 0,
    AllowPageShuffle: true,
    ItemTransform: "1 0 0 1 0 0",
  });
  element(spread, "Page", {
    Self: "page",
    Name: "1",
    AppliedMaster: "n",
    GeometricBounds: `0 0 ${height} ${width}`,
    ItemTransform: `1 0 0 1 0 ${-height / 2}`,
    MasterPageTransform: "1 0 0 1 0 0",
  });
  add("Spreads/Spread.xml", spreadPackage);

  async function toBlob() {
    const zip = new ZipWriter(new BlobWriter(IDML_MIME_TYPE));
    // IDML requires the MIME entry first and uncompressed, plus Adobe's document recognition PI.
    await zip.add("mimetype", new TextReader(IDML_MIME_TYPE), { level: 0 });
    await zip.add(
      "designmap.xml",
      new TextReader(
        xml(root).replace(
          "?>",
          '?>\n<?aid style="50" type="document" readerVersion="6.0" featureSet="257" product="7.0" ?>',
        ),
      ),
    );
    for (const [path, resource] of files)
      await zip.add(path, new TextReader(xml(resource)));
    await zip.add(
      "META-INF/container.xml",
      new TextReader(
        '<?xml version="1.0" encoding="UTF-8"?><container version="1.0" xmlns="urn:oasis:names:tc:opendocument:xmlns:container"><rootfiles><rootfile full-path="designmap.xml" media-type="text/xml"/></rootfiles></container>',
      ),
    );
    return zip.close();
  }

  return { root, graphic, fonts, spread, add, toBlob };
}
